import React, { useEffect, useState, useCallback } from 'react';
import { internalScheduleAPI, filesAPI, hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Upload, FileText, CalendarClock, ChevronDown, ChevronRight, Loader2,
} from 'lucide-react';

const UNIT_DAYS = { days: 1, months: 30, years: 365 };

const daysToUnit = (days) => {
  if (days % 365 === 0) return { value: days / 365, unit: 'years' };
  if (days % 30 === 0) return { value: days / 30, unit: 'months' };
  return { value: days, unit: 'days' };
};

export default function InternalSchedulesTab({ toolId, onChanged }) {
  const { canEdit, isAdmin } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [recordsMap, setRecordsMap] = useState({});

  const [schedDialogOpen, setSchedDialogOpen] = useState(false);
  const [editingSched, setEditingSched] = useState(null);
  const [schedForm, setSchedForm] = useState({
    name: '', freqValue: 3, freqUnit: 'months', recall_lead_days: 7,
    last_done_date: '', method: '', standard_used: '', acceptance_criteria: '', remark: '',
  });
  const [savingSched, setSavingSched] = useState(false);

  const [recDialogOpen, setRecDialogOpen] = useState(false);
  const [recSchedule, setRecSchedule] = useState(null);
  const [recForm, setRecForm] = useState({
    calibration_date: new Date().toISOString().substring(0, 10),
    performed_by: '', performed_by_id: '', result: 'pass', notes: '',
  });
  const [recFile, setRecFile] = useState(null);
  const [savingRec, setSavingRec] = useState(false);

  const loadSchedules = useCallback(async () => {
    try {
      const res = await internalScheduleAPI.list(toolId);
      setSchedules(res.data || []);
    } catch {
      toast.error('Failed to load internal schedules');
    } finally {
      setLoading(false);
    }
  }, [toolId]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  useEffect(() => {
    hrAPI.listEmployees()
      .then(res => setEmployees((res.data || []).filter(e => (e.status || 'active') !== 'inactive')))
      .catch(() => {});
  }, []);

  const loadRecords = async (scheduleId) => {
    try {
      const res = await internalScheduleAPI.listRecords(scheduleId);
      setRecordsMap(prev => ({ ...prev, [scheduleId]: res.data || [] }));
    } catch {
      toast.error('Failed to load records');
    }
  };

  const toggleExpand = (scheduleId) => {
    const next = !expanded[scheduleId];
    setExpanded(prev => ({ ...prev, [scheduleId]: next }));
    if (next && !recordsMap[scheduleId]) loadRecords(scheduleId);
  };

  const openAddSchedule = () => {
    setEditingSched(null);
    setSchedForm({
      name: '', freqValue: 3, freqUnit: 'months', recall_lead_days: 7,
      last_done_date: '', method: '', standard_used: '', acceptance_criteria: '', remark: '',
    });
    setSchedDialogOpen(true);
  };

  const openEditSchedule = (s) => {
    const { value, unit } = daysToUnit(s.frequency_days);
    setEditingSched(s);
    setSchedForm({
      name: s.name || '', freqValue: value, freqUnit: unit,
      recall_lead_days: s.recall_lead_days ?? 7,
      last_done_date: s.last_done_date ? s.last_done_date.substring(0, 10) : '',
      method: s.method || '', standard_used: s.standard_used || '',
      acceptance_criteria: s.acceptance_criteria || '', remark: s.remark || '',
    });
    setSchedDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!schedForm.name.trim()) { toast.error('Schedule name is required'); return; }
    const freqValue = parseInt(schedForm.freqValue) || 0;
    if (freqValue <= 0) { toast.error('Frequency must be greater than 0'); return; }
    const frequency_days = freqValue * UNIT_DAYS[schedForm.freqUnit];
    const frequency_label = `${freqValue} ${schedForm.freqUnit.charAt(0).toUpperCase() + schedForm.freqUnit.slice(1)}`;
    const payload = {
      name: schedForm.name.trim(),
      frequency_days,
      frequency_label,
      recall_lead_days: parseInt(schedForm.recall_lead_days) || 0,
      last_done_date: schedForm.last_done_date ? new Date(schedForm.last_done_date).toISOString() : null,
      method: schedForm.method, standard_used: schedForm.standard_used,
      acceptance_criteria: schedForm.acceptance_criteria, remark: schedForm.remark,
    };
    setSavingSched(true);
    try {
      if (editingSched) {
        await internalScheduleAPI.update(editingSched.id, { ...payload, status: editingSched.status });
        toast.success('Internal schedule updated');
      } else {
        await internalScheduleAPI.create(toolId, payload);
        toast.success('Internal schedule added');
      }
      setSchedDialogOpen(false);
      loadSchedules();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSavingSched(false);
    }
  };

  const handleDeleteSchedule = async (s) => {
    if (!window.confirm(`Delete internal schedule "${s.name}" and all its records?`)) return;
    try {
      await internalScheduleAPI.remove(s.id);
      toast.success('Schedule deleted');
      loadSchedules();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openAddRecord = (s) => {
    setRecSchedule(s);
    setRecForm({
      calibration_date: new Date().toISOString().substring(0, 10),
      performed_by: '', performed_by_id: '', result: 'pass', notes: '',
    });
    setRecFile(null);
    setRecDialogOpen(true);
  };

  const handleSaveRecord = async () => {
    if (!recForm.calibration_date || !recForm.performed_by.trim()) {
      toast.error('Calibration date and performed-by are required');
      return;
    }
    setSavingRec(true);
    try {
      const res = await internalScheduleAPI.addRecord(recSchedule.id, {
        calibration_date: new Date(recForm.calibration_date).toISOString(),
        performed_by: recForm.performed_by.trim(),
        result: recForm.result,
        notes: recForm.notes,
      });
      const recordId = res.data.id;
      if (recFile && recordId) {
        const fd = new FormData();
        fd.append('file', recFile);
        fd.append('entity_type', 'internal_cal_record');
        fd.append('entity_id', recordId);
        fd.append('file_category', 'internal_record');
        await filesAPI.upload(fd);
      }
      toast.success(res.data.instrument_restored
        ? 'Record added. Instrument restored to Active.'
        : 'Internal calibration record added. Next due recalculated.');
      setRecDialogOpen(false);
      loadSchedules();
      loadRecords(recSchedule.id);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add record');
    } finally {
      setSavingRec(false);
    }
  };

  const dueBadge = (s) => {
    if (!s.next_due_date) return null;
    const due = new Date(s.next_due_date);
    const now = new Date();
    const days = Math.ceil((due - now) / 86400000);
    if (days < 0) return <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">Overdue {Math.abs(days)}d</Badge>;
    if (days <= (s.recall_lead_days ?? 7)) return <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Due in {days}d</Badge>;
    return <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Due in {days}d</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32 text-sm text-slate-400">Loading schedules...</div>;
  }

  return (
    <div data-testid="internal-schedules-tab">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">Internal Calibration Schedules</h3>
          <p className="text-[11px] text-slate-500">In-house checks (e.g. Volumetric Verification, Artefact Verification) with their own frequency and recall lead time.</p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={openAddSchedule} className="text-xs gap-1 bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="add-internal-schedule-button">
            <Plus className="w-3 h-3" /> Add Schedule
          </Button>
        )}
      </div>

      {schedules.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No internal calibration schedules. Add one (e.g. a 3-monthly volumetric check).</p>
      ) : (
        <div className="space-y-3">
          {schedules.map(s => (
            <div key={s.id} className="border border-slate-200 rounded-md" data-testid={`internal-sched-${s.id}`}>
              <div className="p-3 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-start gap-2 min-w-0">
                  <button onClick={() => toggleExpand(s.id)} className="mt-0.5 text-slate-400 hover:text-slate-700" data-testid={`expand-sched-${s.id}`}>
                    {expanded[s.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CalendarClock className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-sm font-medium text-[#0F172A]">{s.name}</span>
                      {dueBadge(s)}
                      {s.status === 'inactive' && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>}
                    </div>
                    <div className="flex items-center gap-x-4 gap-y-0.5 mt-1 text-[11px] text-slate-500 flex-wrap">
                      <span>Every <b className="text-slate-700">{s.frequency_label}</b></span>
                      <span>Recall <b className="text-slate-700">{s.recall_lead_days}d</b> before due</span>
                      <span>Last: <b className="text-slate-700 font-mono">{s.last_done_date ? s.last_done_date.substring(0, 10) : '—'}</b></span>
                      <span>Next due: <b className="text-slate-700 font-mono">{s.next_due_date ? s.next_due_date.substring(0, 10) : '—'}</b></span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {canEdit && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => openAddRecord(s)} data-testid={`add-record-${s.id}`}>
                        <Plus className="w-3 h-3" /> Record
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditSchedule(s)} data-testid={`edit-sched-${s.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDeleteSchedule(s)} data-testid={`delete-sched-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>

              {expanded[s.id] && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-3">
                  <p className="text-[11px] font-semibold text-slate-500 mb-2">Calibration Records</p>
                  <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/80">
                          <TableHead className="text-xs font-semibold text-slate-500">Date</TableHead>
                          <TableHead className="text-xs font-semibold text-slate-500">Performed By</TableHead>
                          <TableHead className="text-xs font-semibold text-slate-500">Result</TableHead>
                          <TableHead className="text-xs font-semibold text-slate-500">Next Due</TableHead>
                          <TableHead className="text-xs font-semibold text-slate-500">Notes</TableHead>
                          <TableHead className="text-xs font-semibold text-slate-500 w-[100px]">Files</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {!recordsMap[s.id] ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-4 text-xs text-slate-400">Loading...</TableCell></TableRow>
                        ) : recordsMap[s.id].length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-4 text-xs text-slate-400">No records yet</TableCell></TableRow>
                        ) : recordsMap[s.id].map(r => (
                          <TableRow key={r.id} data-testid={`rec-row-${r.id}`}>
                            <TableCell className="font-mono text-xs">{r.calibration_date?.substring(0, 10)}</TableCell>
                            <TableCell className="text-xs">{r.performed_by}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${r.result === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.result === 'fail' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{r.result}</Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-500">{r.next_due_date?.substring(0, 10)}</TableCell>
                            <TableCell className="text-xs text-slate-500 max-w-[160px] truncate">{r.notes || '-'}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 flex-wrap">
                                {(r.file_ids || []).map((fid, i) => (
                                  <a key={fid} href={filesAPI.getUrl(fid)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline" data-testid={`rec-file-${r.id}-${i}`}>
                                    <FileText className="w-3 h-3" /> Doc{(r.file_ids.length > 1) ? ` ${i + 1}` : ''}
                                  </a>
                                ))}
                                {(!r.file_ids || r.file_ids.length === 0) && <span className="text-[10px] text-slate-300">—</span>}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Schedule Dialog */}
      <Dialog open={schedDialogOpen} onOpenChange={setSchedDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="internal-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold">{editingSched ? 'Edit Internal Schedule' : 'Add Internal Schedule'}</DialogTitle>
            <DialogDescription className="text-xs">Define an in-house calibration check with its own frequency and recall lead time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Schedule Name *</Label>
              <Input value={schedForm.name} onChange={e => setSchedForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Volumetric Verification" className="h-8 text-sm" data-testid="sched-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Frequency *</Label>
                <div className="flex gap-2">
                  <Input type="number" min="1" value={schedForm.freqValue} onChange={e => setSchedForm(p => ({ ...p, freqValue: e.target.value }))} className="h-8 text-sm w-16" data-testid="sched-freq-value" />
                  <Select value={schedForm.freqUnit} onValueChange={v => setSchedForm(p => ({ ...p, freqUnit: v }))}>
                    <SelectTrigger className="h-8 text-sm flex-1" data-testid="sched-freq-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="months">Months</SelectItem>
                      <SelectItem value="years">Years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Recall Lead (days) *</Label>
                <Input type="number" min="0" value={schedForm.recall_lead_days} onChange={e => setSchedForm(p => ({ ...p, recall_lead_days: e.target.value }))} className="h-8 text-sm" data-testid="sched-recall-lead" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Last Done Date</Label>
              <Input type="date" value={schedForm.last_done_date} onChange={e => setSchedForm(p => ({ ...p, last_done_date: e.target.value }))} className="h-8 text-sm" data-testid="sched-last-done" />
              <p className="text-[10px] text-slate-400">Next due is auto-calculated from this date + frequency. Leave blank to start from today.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Method</Label>
                <Input value={schedForm.method} onChange={e => setSchedForm(p => ({ ...p, method: e.target.value }))} placeholder="Optional" className="h-8 text-sm" data-testid="sched-method" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Standard Used</Label>
                <Input value={schedForm.standard_used} onChange={e => setSchedForm(p => ({ ...p, standard_used: e.target.value }))} placeholder="Optional" className="h-8 text-sm" data-testid="sched-standard" />
              </div>
            </div>
            {editingSched && (
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={editingSched.status} onValueChange={v => setEditingSched(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="sched-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive (pause recalls)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSchedDialogOpen(false)} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSaveSchedule} disabled={savingSched} className="text-xs bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="save-sched-button">
              {savingSched && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {editingSched ? 'Update' : 'Add Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Record Dialog */}
      <Dialog open={recDialogOpen} onOpenChange={setRecDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="internal-record-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold">Record Internal Calibration</DialogTitle>
            <DialogDescription className="text-xs">
              {recSchedule?.name} — next due recalculates from this date + {recSchedule?.frequency_label}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Calibration Date *</Label>
                <Input type="date" value={recForm.calibration_date} onChange={e => setRecForm(p => ({ ...p, calibration_date: e.target.value }))} className="h-8 text-sm" data-testid="rec-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Result</Label>
                <Select value={recForm.result} onValueChange={v => setRecForm(p => ({ ...p, result: v }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="rec-result-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass">Pass</SelectItem>
                    <SelectItem value="fail">Fail</SelectItem>
                    <SelectItem value="adjusted">Adjusted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Performed By (Technician) *</Label>
              <SearchableSelect
                options={employees.map(e => ({ value: e.id, label: e.emp_code ? `${e.name} (${e.emp_code})` : e.name, searchStr: `${e.name} ${e.emp_code || ''} ${e.email || ''}` }))}
                value={recForm.performed_by_id}
                onSelect={v => { const emp = employees.find(x => x.id === v); setRecForm(p => ({ ...p, performed_by_id: v, performed_by: emp ? emp.name : '' })); }}
                placeholder="Search technician..." emptyText="No employees found." testId="rec-performed-by"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Input value={recForm.notes} onChange={e => setRecForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" className="h-8 text-sm" data-testid="rec-notes" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Upload className="w-3 h-3" /> Attach Record (PDF/Image)</Label>
              <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={e => setRecFile(e.target.files[0])} className="h-8 text-sm" data-testid="rec-file-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRecDialogOpen(false)} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSaveRecord} disabled={savingRec} className="text-xs bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="save-record-button">
              {savingRec && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Save Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
